export class SectionPlayer {
  private element: HTMLAudioElement;
  private endedCallback: (() => void) | null = null;
  private boundEndedHandler: (() => void) | null = null;
  private loadCleanup: (() => void) | null = null;

  constructor(audioElement: HTMLAudioElement) {
    this.element = audioElement;
  }

  load(url: string): Promise<void> {
    // Clean up listeners from any previous load
    this.loadCleanup?.();

    return new Promise<void>((resolve, reject) => {
      const onCanPlay = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        reject(new Error('Failed to load audio'));
      };

      const cleanup = () => {
        this.element.removeEventListener('canplaythrough', onCanPlay);
        this.element.removeEventListener('error', onError);
        this.loadCleanup = null;
      };

      this.loadCleanup = cleanup;

      this.element.addEventListener('canplaythrough', onCanPlay);
      this.element.addEventListener('error', onError);
      this.element.src = url;
    });
  }

  play(): void {
    this.element.play();
  }

  pause(): void {
    this.element.pause();
  }

  get currentTime(): number {
    return this.element.currentTime * 1000;
  }

  set currentTime(ms: number) {
    this.element.currentTime = ms / 1000;
  }

  get duration(): number {
    const d = this.element.duration;
    return Number.isNaN(d) ? 0 : d * 1000;
  }

  get volume(): number {
    return this.element.volume;
  }

  set volume(v: number) {
    this.element.volume = v;
  }

  get playbackRate(): number {
    return this.element.playbackRate;
  }

  set playbackRate(rate: number) {
    this.element.playbackRate = rate;
  }

  get isPlaying(): boolean {
    return !this.element.paused;
  }

  stop(): void {
    this.element.pause();
    this.element.currentTime = 0;
  }

  onEnded(callback: () => void): void {
    // Remove previous ended listener if any
    if (this.boundEndedHandler) {
      this.element.removeEventListener('ended', this.boundEndedHandler);
    }
    this.endedCallback = callback;
    this.boundEndedHandler = () => this.endedCallback?.();
    this.element.addEventListener('ended', this.boundEndedHandler);
  }

  destroy(): void {
    this.element.pause();
    this.element.currentTime = 0;
    this.loadCleanup?.();
    if (this.boundEndedHandler) {
      this.element.removeEventListener('ended', this.boundEndedHandler);
      this.boundEndedHandler = null;
    }
    this.endedCallback = null;
  }
}
